.shell {
  display: flex;
  flex-direction: column;
  block-size: var(--tg-viewport);
  padding-block-start: var(--safe-top);
  background-color: var(--bg);
  overflow: hidden;
}

/* The wall's weave. One tiled layer on the shell only: linen is textured, the
   paper laid on it is not. Never intercepts a tap. */
.shell::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: var(--grain);
  opacity: var(--grain-opacity);
}

.main {
  flex: 1;
  min-block-size: 0;
  position: relative;
  z-index: 1;
}

/* Each route is its own scroll container so a transition never scrolls two at once. */
.scroller {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

.withTabs {
  padding-block-end: calc(var(--tabbar-h) + var(--safe-bottom));
}

/* ------------------------------------------------------------------ tabs -- */
.tabbar {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 30;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  block-size: calc(var(--tabbar-h) + var(--safe-bottom));
  padding-block-end: var(--safe-bottom);
  /* Surface comes from the global `.chrome` class; the bar keeps only its own
     lit edge and the shadow it throws down the screen. */
  box-shadow:
    0 -1px 0 var(--paper-edge) inset,
    0 -12px 24px -20px color-mix(in srgb, var(--ink) 40%, transparent);
}

/* The rule above the tabs fades out before the screen edge. */
.tabbar::before {
  content: "";
  position: absolute;
  inset-inline: 0;
  inset-block-start: 0;
  block-size: 1px;
  background-image: var(--hairline);
  pointer-events: none;
}

.tab {
  position: relative;
  display: grid;
  place-items: center;
  gap: 2px;
  color: var(--ink-muted);
  font-size: var(--text-caption);
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: color var(--duration-ui) var(--ease);
}

.tabActive {
  color: var(--accent);
  font-weight: 600;
}

.tabIcon {
  display: grid;
  place-items: center;
}

/* The one mark that travels between tabs; a single layoutId owns it. */
.tabMark {
  position: absolute;
  inset-block-start: 0;
  inline-size: 26px;
  block-size: 2px;
  border-radius: var(--radius-pill);
  background: var(--accent);
}

/* ---------------------------------------------------------------- header -- */
.header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-block-size: var(--header-h);
  padding-inline: var(--space-4);
}

.headerTitle {
  flex: 1;
  min-inline-size: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerActions {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  /* Holds the trailing edge whether or not the row carries a title. */
  margin-inline-start: auto;
}

/* Sticks so search stays reachable while a long list scrolls under it. */
.stickyTop {
  position: sticky;
  inset-block-start: 0;
  z-index: 10;
  /* Chrome over the wall rather than over a card, so the linen shows through
     the blur instead of paper. Surface itself comes from `.chrome`. */
  --chrome-tint: var(--bg);
}

/*
 * The hairline under a sticky header exists only once something has scrolled
 * under it — at rest the chrome should not draw a line across the wall.
 * Scroll-driven, so there is no listener and no re-render per frame; where the
 * WebView lacks the timeline the line simply stays off, which is the resting
 * design and reads as complete on its own.
 */
.stickyTop::after {
  content: "";
  position: absolute;
  inset-inline: 0;
  inset-block-end: -1px;
  block-size: 1px;
  background-image: var(--hairline);
  opacity: 0;
  pointer-events: none;
}

@supports (animation-timeline: scroll()) {
  .stickyTop::after {
    animation: hairline-in linear both;
    animation-timeline: scroll(nearest block);
    animation-range: 0 20px;
  }
}

/* The form pins its own line: its header is a title bar, not a masthead. */
.headerBorder {
  position: relative;
}

.headerBorder::after {
  content: "";
  position: absolute;
  inset-inline: 0;
  inset-block-end: -1px;
  block-size: 1px;
  background-image: var(--hairline);
  opacity: 1;
  animation: none;
}

/*
 * A record's controls belong on the plate, not on a bar above it: floating the
 * row gives the artwork the full width of the screen from the very top edge,
 * which is the whole point of the screen. The buttons carry their own ground
 * because they can land on any picture.
 */
.headerOverlay {
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  z-index: 5;
}

/* Ground, highlight and ink all come from `.chrome-media`; the button only
   says what changes when a finger is on it. */
.headerOverlay button:active {
  background: var(--media-ground-pressed);
}

/* ------------------------------------------------------------ sticky CTA -- */
.ctaBar {
  position: fixed;
  inset-inline: 0;
  inset-block-end: 0;
  z-index: 25;
  padding: var(--space-3) var(--space-4);
  padding-block-end: calc(var(--space-3) + var(--safe-bottom));
  /* A blurred bar reads as a held instrument where a gradient fade read as the
     page dissolving — and it no longer bands on a warm background. Surface
     comes from `.chrome`; the wall shows through it, not paper. */
  --chrome-tint: var(--bg);
}

.ctaBar::before {
  content: "";
  position: absolute;
  inset-inline: 0;
  inset-block-start: 0;
  block-size: 1px;
  background-image: var(--hairline);
  pointer-events: none;
}

/* ------------------------------------------------------------------ page -- */
.page {
  padding-inline: var(--space-4);
}

/*
 * A section is a card in the ledger: its name is written on the linen above it,
 * the record itself sits on paper. Detail and the form share this exactly —
 * one grouping, so a field looks the same whether you are reading it or
 * writing it.
 */
.section {
  display: grid;
  gap: var(--space-2);
  padding-block-start: var(--space-5);
}

.sectionHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding-inline: var(--space-1);
  color: var(--ink-muted);
}

/* The rule runs from the label to the edge of the column, the way a catalogue
   heading is ruled off from the entry beneath it. */
.sectionRule {
  flex: 1;
  block-size: 1px;
  background: var(--border);
  margin-inline-start: var(--space-1);
}

.sectionCard {
  display: grid;
  gap: var(--space-4);
  padding: var(--space-4);
  border-radius: var(--radius-card);
}
